import { useState } from 'react';
import { Button, Group, Select, Stack, TextInput } from '@mantine/core';
import { SubscriberRelationship } from '@bariendo/lib/insurance-coverage';
import type { SubscriberInput } from '../../../../../../bots/src/handlers/update-insurance-coverage';
import { isPastBirthDate, SUBSCRIBER_RELATIONSHIP_OPTIONS, SubscriberFormValues, subscriberInput } from './subscriber';

interface SubscriberFormProps {
  initial: SubscriberFormValues;
  /** Rejects if the write fails; the form stays open holding what was entered. */
  onSave: (input: SubscriberInput) => Promise<void>;
  /** Omitted when there is nothing to go back to: no Cancel is offered. */
  onCancel?: () => void;
  /**
   * The relationship is already decided elsewhere and not offered here: the PN
   * card's Whose plan answers it (BAR-2998). The form keeps `initial`'s.
   */
  relationshipLocked?: boolean;
}

/**
 * The policyholder's four fields: relationship, first name, last name, birth date. For the
 * patient's own plan only the relationship applies. Save stays disabled until the bot would
 * accept the input. Knows nothing of where it sits, so the PN card can use it too.
 */
export function SubscriberForm({ initial, onSave, onCancel, relationshipLocked = false }: SubscriberFormProps) {
  const [values, setValues] = useState<SubscriberFormValues>(initial);
  const [saving, setSaving] = useState(false);
  const input = subscriberInput(values);
  // Saving what is already on file would still write a new version and a Provenance.
  const unchanged = JSON.stringify(input) === JSON.stringify(subscriberInput(initial));
  const birthDateInvalid = values.birthDate !== '' && !isPastBirthDate(values.birthDate);
  const someoneElse = !!values.relationship && values.relationship !== 'self';
  const set = (patch: Partial<SubscriberFormValues>): void => setValues((current) => ({ ...current, ...patch }));

  const save = async (): Promise<void> => {
    if (!input) return;
    setSaving(true);
    try {
      await onSave(input);
    } catch {
      // The caller reports the failure; the form stays open.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap={6}>
      {!relationshipLocked && (
        <Select
          size="xs"
          aria-label="Relationship"
          data-testid="subscriber-relationship"
          data={SUBSCRIBER_RELATIONSHIP_OPTIONS}
          value={values.relationship ?? null}
          onChange={(next) => set({ relationship: (next as SubscriberRelationship | null) ?? undefined })}
          comboboxProps={{ withinPortal: true }}
          disabled={saving}
        />
      )}
      {someoneElse && (
        <>
          <Group gap={6} grow>
            <TextInput
              size="xs"
              aria-label="First name"
              placeholder="First name"
              data-testid="subscriber-first-name"
              value={values.firstName}
              onChange={(event) => set({ firstName: event.currentTarget.value })}
              disabled={saving}
            />
            <TextInput
              size="xs"
              aria-label="Last name"
              placeholder="Last name"
              data-testid="subscriber-last-name"
              value={values.lastName}
              onChange={(event) => set({ lastName: event.currentTarget.value })}
              disabled={saving}
            />
          </Group>
          <TextInput
            size="xs"
            type="date"
            aria-label="Date of birth"
            data-testid="subscriber-birth-date"
            max={todayLocal()}
            error={birthDateInvalid ? 'Enter a past date' : undefined}
            value={values.birthDate}
            onChange={(event) => set({ birthDate: event.currentTarget.value })}
            disabled={saving}
          />
        </>
      )}
      <Group gap={6}>
        <Button
          size="compact-xs"
          data-testid="subscriber-save"
          disabled={!input || unchanged}
          loading={saving}
          onClick={save}
        >
          Save
        </Button>
        {onCancel && (
          <Button
            size="compact-xs"
            variant="subtle"
            data-testid="subscriber-cancel"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </Button>
        )}
      </Group>
    </Stack>
  );
}

/** Today as `YYYY-MM-DD` in the user's timezone, for the date input's `max`. */
function todayLocal(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
